import React, { useState } from 'react';
import {
    Button, CircularProgress,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material";
import { Dialog } from "@mui/material";
import { DialogProps } from "@mui/material/Dialog/Dialog";
import { useRemoveAddressMutation } from "../../../services/grace";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useAlertDispatch } from '../../../context/AlertContext/AlertContext';

export interface ConfirmationDialogProps extends Omit<DialogProps, "onClose"> {
    text: string;
    removeItems: Array<string>;
    onClose: () => void;
    onSubmit: () => void;
    accountId: string;
}

export default function AddressRemoveDialog(props: ConfirmationDialogProps): JSX.Element {
    const {
        text,
        removeItems,
        onClose,
        onSubmit,
        accountId,
        ...other
    } = props;

    const [removeAddress] = useRemoveAddressMutation();
    const [inProgress, setInProgress] = useState(false);
    const alertDispatch = useAlertDispatch();

    const handleClose = () => {
        if (!inProgress) {
            onClose();
        }
    };

    const handleSubmit = async () => {
        if (removeItems.length < 0) {
            onSubmit();
            return;
        }

        try {
            setInProgress(true);
            await removeAddress({ id: removeItems[0], accountId }).unwrap();
            onSubmit();
        } catch (error) {
            const err = error as FetchBaseQueryError;

            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "general",
                        message: "Failed to remove address",
                    }
                });
            }
        }

        setInProgress(false);
    };

    return (
        <Dialog
            onClose={handleClose}
            {...other}
        >
            <DialogTitle>{"Delete address?"}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {text}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                {inProgress &&
                    <CircularProgress size={20} color={"secondary"} />
                }
                <Button name="no" disabled={inProgress} onClick={handleClose} color="primary">
                    No
                </Button>
                <Button name="yes" disabled={inProgress} onClick={handleSubmit} color="primary" autoFocus>
                    Yes
                </Button>
            </DialogActions>
        </Dialog>
    );
}
